// The homepage's words for each platform brand.
//
// The domain picks the brand (brand.brand_key; see theme/brands.js), and each brand speaks to
// its own audience: joulelingo.com to schools, kilosight.com to businesses, hospitality,
// landlords and households. Kilosight copy must never mention schools: people tracking their
// own home asked not to see the word, and registry.test.js-style checks in homeCopy.test.js
// hold it to that. 'power' is the pre-launch default host and keeps the original all-audience
// copy.
//
// `{product}` is replaced with the resolved product name, so a partner on a brand's subdomain
// gets its own name in the same sentences.

const POWER = {
    metaDescription:
        '{product} turns your electricity and gas readings into targets, alerts, cost and carbon reporting for schools, landlords, multi-site businesses and partners.',
    eyebrow: 'Energy transparency platform',
    title: 'Understand, share and reduce energy use across your buildings',
    subtitle:
        '{product} connects to your energy suppliers and smart meters, then turns a reading every half hour into the targets, alerts and reports the people in your buildings act on.',
    outcomePoints: [
        'Waste found where nobody was looking: overnight, at weekends, and across holidays and closures',
        'Weather-normalised comparison, so a cold quarter is not mistaken for a failed intervention',
        'Reporting that is ready for boards, governors, owners and funders without rebuilding a spreadsheet'
    ],
    titleHighlight: null,
    spotlight: {
        media: 'household',
        eyebrow: 'At home',
        title: 'Just tracking your own house?',
        body: '{product} works for a single property too. Connect your smart meter and you get the same half-hourly detail scaled down to one home: where the energy actually goes, what it costs on your tariff, early warning when something unusual starts drawing power, and a what-if slider for trying a change before you commit to it. The school-shaped parts (house competitions, class projects, quizzes) simply are not there.'
    },
    ctaTitle: 'Bring energy transparency to your buildings',
    ctaBody:
        'Start with a single building or roll out across a whole portfolio, with support from onboarding through to adoption.',
    footerTagline:
        'Energy monitoring and reporting for schools, estates and multi-site businesses.'
}

// Playful on purpose (Ross, 22 Oct): pupils and teachers read this too, and the product's
// education side (quiz, activities, datasets, houses) leads.
const JOULELINGO = {
    metaDescription:
        '{product} turns your school’s own meter readings into a weekly energy quiz, classroom activities, real datasets for lessons and a house competition, plus the targets, alerts and carbon reports the business manager needs.',
    eyebrow: 'Energy for schools',
    title: 'Turn your school’s energy into a game everyone can win',
    titleHighlight: 'a game everyone can win',
    subtitle:
        '{product} reads your school’s meters every half hour and turns them into a weekly quiz, classroom activities and a house competition pupils actually care about, and into targets, alerts and reports for the people paying the bills.',
    outcomePoints: [
        'Pupils learning maths, science and geography from the buildings they sit in, not a textbook example',
        'Waste found where nobody was looking: overnight, at weekends and through the holidays',
        'Reporting that is ready for governors, trusts and funders without rebuilding a spreadsheet'
    ],
    spotlight: {
        media: 'classroom',
        eyebrow: 'In the classroom',
        title: 'Lessons with your own school in them',
        body: 'Every Monday there is a new ten-question quiz built from your own meters, pitched for ages 7–11, 11–14 or 14–16, with a leaderboard by pupil and by house. Between quizzes there are ready-made activities for every key stage, real datasets for maths and science lessons, and a house competition where the smallest house can beat the biggest by cutting the most. Teachers get a class mode for the board and a printable worksheet, and see which questions tripped the class up.'
    },
    ctaTitle: 'Bring energy to life at your school',
    ctaBody:
        'Start with one school or roll out across a whole trust. We will show you round with your own school’s readings, quiz and all.',
    footerTagline: 'Energy your whole school can see, play with and save.'
}

// DRAFT for Ross to edit (16 Oct): first pass at the Kilosight voice.
const KILOSIGHT = {
    metaDescription:
        '{product} turns your electricity and gas readings into alerts, cost and carbon reporting for multi-site businesses, hospitality, landlords and homes.',
    eyebrow: 'Energy insight for business',
    title: 'See where your energy goes, and what it costs you',
    subtitle:
        '{product} connects to the meters you already have and turns a reading every half hour into the alerts, comparisons and cost reports that show which sites are wasting energy, and when.',
    outcomePoints: [
        'Waste found where nobody was looking: overnight, at weekends and while a site is closed, measured against each site’s own opening hours',
        'Weather-normalised comparison, so a cold quarter is not mistaken for a failed intervention',
        'Recharge statements for tenants and a login for each site manager, built from the same meter data'
    ],
    // Shown in indigo over a copper marker on kilosight.com's own homepage.
    titleHighlight: 'what it costs you',
    spotlight: {
        media: 'household',
        eyebrow: 'At home',
        title: 'Just tracking your own home?',
        body: '{product} works for a single property too. Connect your smart meter and you get the same half-hourly detail scaled down to one home: where the energy actually goes, what it costs on your tariff (Economy 7 and Agile included), how you compare with a typical home, what the house uses while you are away, early warning when something unusual starts drawing power, and a what-if slider for trying a change before you commit to it.'
    },
    ctaTitle: 'Find out what your buildings use when nobody is there',
    ctaBody:
        'Start with a single site or roll out across a whole portfolio, with support from onboarding through to adoption.',
    footerTagline:
        'Energy monitoring and reporting for businesses, landlords and homes.'
}

export const HOME_COPY = {
    power: POWER,
    joulelingo: JOULELINGO,
    kilosight: KILOSIGHT
}

/** The homepage copy for a brand key; anything unknown gets the default host's copy. */
export function homeCopyFor(brandKey) {
    return HOME_COPY[brandKey] || POWER
}

/** Fill `{product}` in one line of copy. */
export function withProduct(text, productName) {
    return text.replaceAll('{product}', productName)
}
